import React from "react"
import { Button, Flex, HStack, Text } from "@chakra-ui/react"
import { useCurrencySymbol } from "hooks"
import { useVariantIds } from "hooks/useVariantIds"
import type {
  MembershipOptionsV2,
  Price,
  ShippingType,
  VariantType,
} from "types"
import { colours } from "@shared/ui.design-system"
import { formatPrice } from "utils/formatPrice"
import { useTypicalPrice } from "hooks/useTypicalPrice"
import { useUser } from "lib/user"
import { WishlistButton } from "./WishlistButton"
import type { PurchaseType } from "components/Shop/PDPPage/types"
import { markHydrationComplete } from "basket_and_checkout/shared/utils/markHydrationComplete.ref"

type Props = {
  variant: VariantType
  onAddToBag?: ({
    includeAccessPass,
    purchaseTypeIdentifier,
    membershipOverride,
  }: {
    includeAccessPass: boolean
    purchaseTypeIdentifier: PurchaseType
    membershipOverride?: MembershipOptionsV2 | undefined
  }) => void
  purchaseType?: "non-member" | "member"
  shippingInfo?: ShippingType[]
  selectedPriceOption?: Price
}

export const ProductActions: React.FC<Props> = ({
  onAddToBag,
  variant,
  // TODO: remove purchaseType - this should be replaced with selectedPriceOption
  purchaseType,
  // TODO: make non-optional once purchaseType is removed
  selectedPriceOption,
  shippingInfo,
}) => {
  const { hasMembership, isMember } = useUser()
  const currencySymbol = useCurrencySymbol()
  const { pricing } = variant

  const { giftMembershipProductId } = useVariantIds()
  const isFreeGiftMembership = variant.id === giftMembershipProductId

  const textString = shippingInfo?.find((el) => el.isDefault)?.description

  const { typicalPrice } = useTypicalPrice(pricing)
  const priceMembership =
    selectedPriceOption?.price && "membership" in selectedPriceOption.price
      ? selectedPriceOption?.price.membership
      : null

  // TODO: remove client side formatPrice
  const priceString = selectedPriceOption
    ? selectedPriceOption.price.priceText
    : formatPrice(
        purchaseType === "member" || hasMembership || isFreeGiftMembership
          ? pricing.members
          : typicalPrice,
        currencySymbol
      )
  return (
    <Flex
      px={{ base: "24px", md: "80px", lg: "0" }}
      py={{ base: "10px", md: "20px", lg: "24px" }}
      position={{ base: "fixed", lg: "initial" }}
      left="0"
      bottom={isMember ? "38px" : 0}
      w="full"
      zIndex={9}
      borderTop={{ base: `1px solid ${colours.secondary.strong}`, lg: "none" }}
      direction="column"
      bg={colours.secondary.light}
    >
      <HStack m="0">
        <HStack gap="0" w="full">
          <Button
            ref={markHydrationComplete}
            title="Add to bag"
            width="full"
            variant="primary"
            m="0 !important"
            borderRadius="0"
            onClick={() => {
              const includeAccessPass =
                purchaseType === "member" ||
                (selectedPriceOption && !priceMembership?.sku) ||
                hasMembership ||
                isFreeGiftMembership // Feels wrong to add a free trial when adding a gift membership but maintaining

              return onAddToBag?.({
                includeAccessPass,
                purchaseTypeIdentifier: includeAccessPass
                  ? "member"
                  : "non-member",
              })
            }}
          >
            <Text
              as="span"
              variant="acuminMedium"
              fontSize={{ base: "1.25rem", lg: "1rem" }}
              mt="0"
              textTransform="none"
              fontWeight="500"
            >
              <Text as="span" mt="0">
                Add To Bag {` `} {priceString}
              </Text>
            </Text>
          </Button>
        </HStack>
        {!isFreeGiftMembership && <WishlistButton variant={variant} />}
      </HStack>

      {!isFreeGiftMembership && textString && (
        <HStack mt={{ base: "4px", lg: "1rem" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.9397 7.20005H7.11382C6.97552 7.19811 6.83823 7.22404 6.71014 7.27632C6.58029 7.32931 6.46263 7.40827 6.36437 7.50834C6.26612 7.60842 6.18933 7.72751 6.13873 7.85831C6.08946 7.98568 6.06601 8.12153 6.06973 8.25798V9.48007H3.5C3.22386 9.48007 3 9.70393 3 9.98007C3 10.2562 3.22386 10.4801 3.5 10.4801H6.06973V11.2701H3.83997C3.56382 11.2701 3.33997 11.4939 3.33997 11.7701C3.33997 12.0462 3.56382 12.2701 3.83997 12.2701H6.06973V13.0603H4.16016C3.88401 13.0603 3.66016 13.2842 3.66016 13.5603C3.66016 13.8364 3.88401 14.0603 4.16016 14.0603H6.06973V15.242C6.0661 15.3736 6.08771 15.5046 6.13343 15.6281C6.18044 15.755 6.25202 15.8715 6.34407 15.9708C6.43612 16.07 6.54684 16.1502 6.6699 16.2066C6.79296 16.2631 6.92593 16.2947 7.06123 16.2997C7.06739 16.2999 7.07356 16.3001 7.07973 16.3001H7.5407C7.73924 16.8706 8.28178 17.28 8.91996 17.28C9.55066 17.28 10.088 16.8801 10.2921 16.3201H14.9397L14.9461 16.32L14.9582 16.3197C15.0523 16.3162 15.1452 16.2999 15.2345 16.2713C15.2998 16.3025 15.3729 16.3201 15.4502 16.3201H16.6478C16.852 16.8801 17.3892 17.28 18.0199 17.28C18.6507 17.28 19.188 16.88 19.3921 16.3199H20.6002C20.8763 16.3199 21.1002 16.096 21.1002 15.8199V12.8099C21.1002 12.722 21.0771 12.6358 21.0331 12.5597L19.6231 10.1197C19.5338 9.9651 19.3688 9.86987 19.1902 9.86987H15.9497V8.25808C15.9534 8.12655 15.9317 7.9955 15.886 7.87203C15.839 7.74507 15.7674 7.62862 15.6754 7.52935C15.5833 7.43008 15.4726 7.34993 15.3496 7.29348C15.2265 7.23704 15.0935 7.2054 14.9582 7.2004C14.9521 7.20017 14.9459 7.20005 14.9397 7.20005ZM15.9497 10.8699V15.262C15.9503 15.2814 15.9502 15.3007 15.9497 15.3201H16.6478C16.8519 14.76 17.3892 14.36 18.0199 14.36C18.6506 14.36 19.1878 14.7599 19.392 15.3199H20.1002V12.944L18.9016 10.8699H15.9497ZM18.0199 16.28C17.7697 16.28 17.5661 16.0802 17.5601 15.8313L17.5602 15.8201L17.5601 15.8088C17.566 15.5599 17.7696 15.36 18.0199 15.36C18.274 15.36 18.4799 15.566 18.4799 15.82C18.4799 16.0741 18.274 16.28 18.0199 16.28ZM9.37984 15.8094L9.37973 15.8201L9.37984 15.8307C9.37416 16.0799 9.17044 16.28 8.91996 16.28C8.66591 16.28 8.45996 16.0741 8.45996 15.82C8.45996 15.566 8.66591 15.36 8.91996 15.36C9.17044 15.36 9.37416 15.5602 9.37984 15.8094ZM10.2921 15.3201H14.9248C14.9275 15.3196 14.9301 15.3188 14.9326 15.3177C14.9362 15.316 14.9394 15.3137 14.9421 15.3108C14.9448 15.3079 14.9469 15.3045 14.9483 15.3008C14.9496 15.2971 14.9502 15.2932 14.9501 15.2893C14.9499 15.2829 14.9497 15.2765 14.9497 15.2701V8.25005C14.9497 8.24365 14.9499 8.23724 14.9501 8.23084C14.9502 8.2269 14.9496 8.22297 14.9483 8.21927C14.9469 8.21558 14.9448 8.21219 14.9421 8.20929C14.9394 8.2064 14.9362 8.20406 14.9326 8.20242C14.9301 8.20127 14.9275 8.20048 14.9248 8.20005H7.09993C7.09585 8.19997 7.09179 8.20063 7.08801 8.20218C7.08423 8.20372 7.0808 8.20602 7.07794 8.20893C7.07508 8.21185 7.07284 8.21532 7.07137 8.21913C7.06989 8.22294 7.06921 8.22701 7.06937 8.23109C7.06961 8.23741 7.06973 8.24373 7.06973 8.25005V9.96341L7.07 9.98007L7.06973 9.99673V11.7545L7.06997 11.7701L7.06973 11.7856V13.5394C7.07001 13.5463 7.07016 13.5533 7.07016 13.5603C7.07016 13.5673 7.07001 13.5743 7.06973 13.5812V15.2501C7.06973 15.2565 7.0696 15.2629 7.06936 15.2693C7.06921 15.2732 7.06983 15.2771 7.0712 15.2808C7.07257 15.2845 7.07466 15.2879 7.07734 15.2908C7.08002 15.2937 7.08325 15.296 7.08683 15.2977C7.08933 15.2988 7.09198 15.2996 7.09468 15.3001H7.55528C7.76481 14.7505 8.29679 14.36 8.91996 14.36C9.55066 14.36 10.088 14.76 10.2921 15.3201Z"
              fill={colours.primary.dark}
            />
          </svg>
          <Text mt="0">{textString}</Text>
        </HStack>
      )}
    </Flex>
  )
}
